import React from 'react';

/**
 * Create a "random" string e.g. for giving an ID to an element
 * @param length string length
 */
export const randomId = (length = 10) =>
    Math.random().toString(32).slice(2, length + 2);

/**
 * Creates a random string e.g. for generating a nonce or authentication state parameter
 */
export const randomString = (length: number): string => {
    const charset = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz-._';
    let result = '';
    while (length > 0) {
        const bytes = new Uint8Array(16);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const random = (window.crypto || window.msCrypto).getRandomValues(bytes);
        random.forEach(c => {
            if (length === 0) {
                return;
            }
            if (c < charset.length) {
                result += charset[c];
                length--;
            }
        });
    }
    return result;
};

/**
 * Split an input string at the first n occurrences of the delimiter
 * @param str input string
 * @param del split delimiter
 * @param n number of cuts to make
 */
export const split = (str: string, del: string, n: number): string[] => {
    const arr = str.split(del);
    return [ ...arr.slice(0, n), arr.slice(n).join(del) ];
};

/**
 * Creates a shorthand from a given username or e-mail
 *
 * Takes the name-part of an e-mail address if applicable and returns the "initials",
 * i.e. the first letters of the first and last "name-part", e.g.
 * `some.random.example@foo.com` => `se`
 * @param {string} userName
 * @returns {string}
 */
export const userNameShorthand = (userName: string): string => {
    const name = userName.split('@')[0];
    const nameParts = name.split(/\.|-|\s/);
    if (nameParts.length === 1) {
        return name[0];
    }
    return nameParts
        .filter(x => !!x)
        .filter((x, i, a) => i === 0 || i === a.length - 1)
        .map(x => x[0])
        .join('');
};

/**
 * Builds a query string from the object of parameters
 */
export const buildQueryString = (args: Record<string, string>): string =>
    Object.entries(args).map(([ k, v ]) => `${k}=${v}`).join('&');

/**
 * Replaces newlines with break tags, creating a react node list
 */
export const nl2br = (inp: string): React.ReactNode[] =>
    inp
        .split('\n')
        .reduce((r: React.ReactNode[], c, x) => r.length > 0 ? [ ...r, <br key={x}/>, c ] : [ c ], []);
